import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePayOS } from "@payos/payos-checkout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import { getSubscriptionPaymentStatusById } from "../../helpers/backend_helper";

interface CheckoutLocationState {
    checkoutUrl?: string;
    paymentTransactionId?: string | number;
}

const SubscriptionCheckoutPage = () => {
    const navigate = useNavigate();
    const { paymentTransactionId } = useParams();
    const location = useLocation();
    const state = (location.state ?? {}) as CheckoutLocationState;

    const paymentTransactionIdRef = useRef<string | number | null>(paymentTransactionId ?? null);
    const openedCheckoutUrlRef = useRef<string>("");
    const returnUrl = `${window.location.origin}/subscription/checkout/${paymentTransactionId ?? ""}`;
    const [statusMessage, setStatusMessage] = useState<string>("Đang chờ thanh toán...");
    const [error, setError] = useState<string>("");

    const [payOSConfig, setPayOSConfig] = useState({
        RETURN_URL: returnUrl,
        ELEMENT_ID: "embedded-payment-container",
        CHECKOUT_URL: state.checkoutUrl ?? "",
        embedded: true,
        onSuccess: async () => {
            setStatusMessage("Đã nhận tín hiệu thanh toán thành công. Đang xác nhận với hệ thống...");
            await verifyPaymentStatus();
        },
    });

    const { open } = usePayOS(payOSConfig);

    const verifyPaymentStatus = async () => {
        if (!paymentTransactionIdRef.current) {
            setError("Không tìm thấy mã giao dịch thanh toán.");
            return;
        }

        for (let attempt = 0; attempt < 8; attempt += 1) {
            try {
                const response: any = await getSubscriptionPaymentStatusById(paymentTransactionIdRef.current);
                const payload = response?.data?.data ?? response?.data ?? {};
                const paymentStatus = String(payload?.paymentStatus ?? payload?.status ?? "").toLowerCase();

                if (paymentStatus === "success") {
                    navigate("/subscription");
                    return;
                }

                if (paymentStatus === "failed" || paymentStatus === "cancelled") {
                    setError("Thanh toán không thành công. Vui lòng thử lại.");
                    return;
                }
            } catch (_error) {
                // webhook/API status is source of truth; continue polling
            }

            await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        setStatusMessage("Đã gửi lệnh thanh toán. Vui lòng đợi thêm vài giây để hệ thống cập nhật trạng thái.");
    };

    useEffect(() => {
        if (!state.checkoutUrl) {
            setError("Thiếu đường dẫn thanh toán. Vui lòng quay lại và tạo giao dịch mới.");
            return;
        }

        setPayOSConfig((previousConfig) => ({
            ...previousConfig,
            RETURN_URL: returnUrl,
            CHECKOUT_URL: state.checkoutUrl ?? "",
        }));
    }, [returnUrl, state.checkoutUrl]);

    useEffect(() => {
        if (!payOSConfig.CHECKOUT_URL) {
            return;
        }

        if (openedCheckoutUrlRef.current === payOSConfig.CHECKOUT_URL) {
            return;
        }

        openedCheckoutUrlRef.current = payOSConfig.CHECKOUT_URL;
        open();
    }, [payOSConfig.CHECKOUT_URL, open]);

    const transactionDisplay = useMemo(() => paymentTransactionId ?? "-", [paymentTransactionId]);

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <div className="back-button me-2">
                        <button
                            type="button"
                            className="btn btn-link p-0 text-dark"
                            onClick={() => navigate("/subscription")}
                            aria-label="Quay lại"
                        >
                            <i className="mdi mdi-arrow-left"></i>
                        </button>
                    </div>

                    <div className="page-heading">
                        <h6 className="mb-0">Thanh toán gói dịch vụ</h6>
                    </div>

                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container py-3">
                    <div className="mb-3">
                        <p className="mb-2">
                            <strong>Mã giao dịch:</strong> #{transactionDisplay}
                        </p>

                        {error ? (
                            <p className="text-danger mb-0">{error}</p>
                        ) : (
                            <p className="text-primary mb-0">{statusMessage}</p>
                        )}
                    </div>

                    <div
                        id="embedded-payment-container"
                        style={{
                            width: "100%",
                            minHeight: "680px",
                            height: "calc(100vh - 180px)",
                            overflow: "auto",
                            background: "#fff",
                            borderRadius: "12px",
                        }}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export default SubscriptionCheckoutPage;
